import { useNavigate } from 'react-router-dom'
import { Building2, Shield, ShieldAlert, LogOut } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'

export default function FirmaSelectPage() {
  const navigate = useNavigate()
  // Firmen IMMER aus dem AuthContext holen — location.state ging bei Refresh
  // und einigen Navigations-Pfaden verloren (Superadmin sah dann nur die
  // Superadmin-Box, nicht seine eigenen AP-Firmen).
  const { isSuperAdmin, user, firmen, logout } = useAuth()
  const hasFirmen = firmen.length > 0

  // Eingeloggt, aber kein portal_users-Eintrag (Orphan): „Kein Zugang"-Hinweis
  if (!hasFirmen && !isSuperAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-fom-500 p-6">
        <div className="w-full max-w-lg bg-white rounded-2xl shadow-modal overflow-hidden">
          <div className="px-10 py-10">
            <div className="flex items-center gap-1.5 mb-8 justify-center">
              <span className="text-gray-900 text-2xl font-display italic font-bold tracking-tight">My</span>
              <div className="bg-fom-500 rounded-md px-2.5 py-1">
                <span className="text-white text-2xl font-display font-black tracking-tight">FOM</span>
              </div>
            </div>
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center">
                <ShieldAlert size={28} className="text-amber-600" />
              </div>
            </div>
            <h2 className="text-xl font-bold text-gray-900 text-center mb-2">Kein Zugriff freigegeben</h2>
            <p className="text-gray-600 text-sm text-center mb-2">
              Sie sind als <span className="font-semibold">{user?.Email}</span> angemeldet,
              haben aber aktuell keinen Zugang zu einem Firmen-Bereich.
            </p>
            <p className="text-gray-500 text-xs text-center mb-8">
              Ihre FOM-Ansprechperson muss Sie für Ihre Firma freischalten.
              Sie wurden vorgemerkt und werden benachrichtigt, sobald der Zugang aktiv ist.
            </p>
            <button
              onClick={() => logout()}
              className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl
                         border border-gray-200 bg-white hover:bg-gray-50 transition-all
                         text-sm font-semibold text-gray-700"
            >
              <LogOut size={16} />
              Abmelden
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-fom-500 p-6">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-modal overflow-hidden">
        <div className="px-10 py-10">
          {/* Logo */}
          <div className="flex items-center gap-1.5 mb-8 justify-center">
            <span className="text-gray-900 text-2xl font-display italic font-bold tracking-tight">My</span>
            <div className="bg-fom-500 rounded-md px-2.5 py-1">
              <span className="text-white text-2xl font-display font-black tracking-tight">FOM</span>
            </div>
          </div>

          <h2 className="text-xl font-bold text-gray-900 text-center mb-2">
            {hasFirmen ? 'Firma auswählen' : 'Bereich auswählen'}
          </h2>
          <p className="text-gray-500 text-sm text-center mb-8">
            {hasFirmen
              ? 'Ihr Konto ist mit mehreren Unternehmen verknüpft. Bitte wählen Sie aus:'
              : 'Sie haben Zugriff auf folgende Bereiche.'}
          </p>

          {hasFirmen && (
            <div className="space-y-3">
              {firmen.map((f) => (
                <button
                  key={f.D365_AccountID}
                  onClick={() => navigate(`/firma/${f.token}`, { replace: true })}
                  className="w-full flex items-center gap-4 px-5 py-4 rounded-xl border border-gray-200
                             bg-white hover:bg-fom-50 hover:border-fom-300 transition-all text-left
                             focus:outline-none focus:ring-2 focus:ring-fom-500 group"
                >
                  <div className="shrink-0 w-10 h-10 rounded-lg bg-fom-100 flex items-center justify-center
                                  group-hover:bg-fom-200 transition-colors">
                    <Building2 size={20} className="text-fom-600" />
                  </div>
                  <span className="text-sm font-semibold text-gray-800 group-hover:text-fom-700 transition-colors">
                    {f.Firmenname}
                  </span>
                </button>
              ))}
            </div>
          )}

          {isSuperAdmin && (
            <>
              {hasFirmen && (
                <div className="flex items-center gap-3 my-6">
                  <div className="flex-1 border-t border-gray-200" />
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Administration</span>
                  <div className="flex-1 border-t border-gray-200" />
                </div>
              )}
              <button
                onClick={() => navigate('/superadmin', { replace: true })}
                className="w-full flex items-center gap-4 px-5 py-4 rounded-xl border border-amber-200
                           bg-amber-50 hover:bg-amber-100 hover:border-amber-300 transition-all text-left
                           focus:outline-none focus:ring-2 focus:ring-amber-500 group"
              >
                <div className="shrink-0 w-10 h-10 rounded-lg bg-amber-200 flex items-center justify-center
                                group-hover:bg-amber-300 transition-colors">
                  <Shield size={20} className="text-amber-700" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-amber-900 group-hover:text-amber-950 transition-colors">
                    Superadmin-Bereich
                  </div>
                  <div className="text-xs text-amber-700/80">Cross-Tenant-Verwaltung · Audit-Log</div>
                </div>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
